import type { Snapshot } from 'pages/SnapshotManagement/snapshotManagementTypes';
import { memo } from 'react';

import { ScrollColumnView } from '@ipfabric/ui-components/ScrollView/ScrollColumnView';
import { useStableCallback } from '@ipfabric/ui-components/hooks/useStableCallback';
import {
  type SnapshotId,
  date,
  rgbColors,
  untitledSnapshotName,
} from '@ipfabric/utils';
import { prop } from '@ipfabric/utils/fp/prop';
import { compareSnapshot } from 'components/Graph/commands/compareSnapshot';
import { selectSnapshotToCompareId } from 'components/Graph/model/selectors/overlay/selectSnapshotToCompareId';
import { SelectSnapshot } from 'components/SelectSnapshot/SelectSnapshot';
import { useLocale } from 'containers/App/LocaleContext';
import { useSnapshots } from 'pages/SnapshotManagement/useSnapshots';
import { useDateTimeFormat } from 'utils/hooks/useDateTimeFormat';
import { useTimezone } from 'utils/hooks/useTimeZone';
import { useGraphContextSelectors } from '../GraphContext';

type GraphCompareSnapshotOption = Snapshot;

const BareGraphCompareSnapshot = () => {
  const ctxProps = useGraphContextSelectors({
    dispatch: prop('dispatch'),
    graphLoaded: (s) => s.graphMetadata !== null,
    snapshotId: prop('snapshotId'),
    snapshotToCompareId: selectSnapshotToCompareId,
  });

  const msg = useLocale();
  const snapshots = useSnapshots();
  const dateTimeFormat = useDateTimeFormat();
  const timezone = useTimezone();

  const handleGraphCompareChange = useStableCallback(
    (snapshotId: SnapshotId | null) => {
      if (snapshotId === null) {
        ctxProps.dispatch(compareSnapshot, { snapshotToCompareId: null });
      } else if (snapshotId !== ctxProps.snapshotToCompareId) {
        ctxProps.dispatch(compareSnapshot, { snapshotToCompareId: snapshotId });
      }
    },
  );

  const foundSnapshot = snapshots.find(
    (snapshot) => snapshot.id === ctxProps.snapshotId,
  );

  if (!foundSnapshot) {
    return null;
  }

  const graphCompareOptions: GraphCompareSnapshotOption[] = snapshots
    ? snapshots.filter(
        (snapshot) => snapshot.state === 'loaded' && snapshot.status === 'done',
      )
    : [];

  const graphCompareValue: GraphCompareSnapshotOption | null =
    graphCompareOptions.find(
      (snapshot: GraphCompareSnapshotOption) =>
        snapshot.id === ctxProps.snapshotToCompareId,
    ) ?? null;

  return (
    <ScrollColumnView>
      <div className='flex flex-1 flex-col p-5'>
        <h4 className='mb-lgu-2 mt-2.5 font-semibold'>
          {msg.canvasGraph.graphSidePanel.compareSnapshots.currentSnapshot}
        </h4>
        <div className='mb-lgu-1 font-semibold'>
          {foundSnapshot.name || untitledSnapshotName}
        </div>
        <div className='mb-lgu-10'>
          {date.formatUtcTimestampInTimeZone(
            new Date(foundSnapshot.tsStart),
            timezone,
            dateTimeFormat,
          )}
        </div>
        <h4 className='mb-lgu-3 mt-0 font-semibold'>
          {msg.canvasGraph.graphSidePanel.compareSnapshots.compareSnapshot}
        </h4>
        <SelectSnapshot
          clearable
          disabled={!ctxProps.graphLoaded}
          onChange={handleGraphCompareChange}
          snapshots={graphCompareOptions}
          value={graphCompareValue?.id ?? null}
        />
        <div>
          <div
            className='mr-lgu-3 inline-block size-5 border border-solid border-gray-900'
            style={{ borderRadius: '10px' }}
          >
            <i className='fa fa-info fa-fw' />
          </div>
          <h4 className='my-lgu-10 inline-block font-semibold'>
            {msg.canvasGraph.graphSidePanel.compareSnapshots.diagramLegend}
          </h4>
        </div>
        <div className='flex'>
          <div className='mr-lgu-5 flex flex-1 items-center'>
            <div
              className='mr-lgu-3 size-2.5'
              style={{ background: rgbColors.green }}
            />
            <div className='leading-lgu-6'>
              {msg.canvasGraph.graphSidePanel.compareSnapshots.elementsAdded}
            </div>
          </div>
          <div className='mr-lgu-5 flex flex-1 items-center'>
            <div
              className='mr-lgu-3 size-2.5'
              style={{ background: rgbColors.red }}
            />
            <div className='leading-lgu-6'>
              {msg.canvasGraph.graphSidePanel.compareSnapshots.elementsRemoved}
            </div>
          </div>
          <div className='flex flex-1 items-center'>
            <div
              className='mr-lgu-3 size-2.5'
              style={{ background: rgbColors.gray }}
            />
            <div className='leading-lgu-6'>
              {msg.canvasGraph.graphSidePanel.compareSnapshots.noChanges}
            </div>
          </div>
        </div>
      </div>
    </ScrollColumnView>
  );
};

export const GraphCompareSnapshot = memo(BareGraphCompareSnapshot);
